import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "../../components/Logo";
import OtpInput from "../../components/OtpInput";
import { usePatientAuth } from "../../store/authPatientStore";

const PENDING_OTP_REQUEST_ID_KEY = "agbaza.patient.login.otpRequestId";
const PENDING_USER_HINT_KEY = "agbaza.patient.login.userHint";

type LoginCodeState = {
  otpRequestId?: string;
  userHint?: string;
  redirect?: string;
};

export default function PatientLoginCode() {
  const navigate = useNavigate();
  const location = useLocation();
  const { confirmLoginOtp } = usePatientAuth();
  const [otpRequestId, setOtpRequestId] = useState("");
  const [userHint, setUserHint] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const state = (location.state as LoginCodeState | null) ?? null;
  const redirectTo = state?.redirect;

  useEffect(() => {
    const fromState = state?.otpRequestId ?? "";
    const fromStorage = sessionStorage.getItem(PENDING_OTP_REQUEST_ID_KEY) ?? "";
    const resolvedOtpRequestId = fromState || fromStorage;

    if (!resolvedOtpRequestId) {
      navigate("/patient/connexion", { replace: true });
      return;
    }

    setOtpRequestId(resolvedOtpRequestId);
    if (!fromStorage && fromState) {
      sessionStorage.setItem(PENDING_OTP_REQUEST_ID_KEY, fromState);
    }

    const hintFromState = state?.userHint ?? "";
    const hintFromStorage = sessionStorage.getItem(PENDING_USER_HINT_KEY) ?? "";
    const resolvedHint = hintFromState || hintFromStorage;
    setUserHint(resolvedHint);
    if (!hintFromStorage && hintFromState) {
      sessionStorage.setItem(PENDING_USER_HINT_KEY, hintFromState);
    }
  }, [navigate, state?.otpRequestId, state?.userHint]);

  const codeValue = code.replace(/\s/g, "");
  const ready = codeValue.length === 6 && !!otpRequestId && !submitting;
  const autoSubmittedRef = useRef(false);

  useEffect(() => {
    if (ready && !autoSubmittedRef.current) {
      autoSubmittedRef.current = true;
      onConfirm();
    }
    if (!ready) autoSubmittedRef.current = false;
  }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps

  const onConfirm = async () => {
    if (!ready || !otpRequestId) return;
    setError("");
    setSubmitting(true);
    const result = await confirmLoginOtp(otpRequestId, codeValue);
    setSubmitting(false);
    if (!result.ok) {
      setError(result.message || "Code invalide ou expiré.");
      return;
    }

    sessionStorage.removeItem(PENDING_OTP_REQUEST_ID_KEY);
    sessionStorage.removeItem(PENDING_USER_HINT_KEY);
    const target = redirectTo && redirectTo.startsWith("/patient/") ? redirectTo : "/patient";
    navigate(target, { replace: true });
  };

  const onBack = () => {
    sessionStorage.removeItem(PENDING_OTP_REQUEST_ID_KEY);
    sessionStorage.removeItem(PENDING_USER_HINT_KEY);
  };

  if (!otpRequestId) return null;

  return (
    <div className="min-h-screen flex flex-col bg-white text-[#0B1F16]">
      <main className="flex-1 w-full max-w-sm mx-auto flex flex-col items-center justify-center py-8 px-4">
        <div className="text-center">
          <Logo size={90} />
          <p className="mt-4 text-lg font-semibold text-[#0B1F16]">Code de connexion</p>
          <p className="mt-1 text-sm text-gray-600">
            {userHint
              ? `Un code OTP a ete envoye a ${userHint}.`
              : "Saisissez le code OTP recu pour finaliser la connexion."}
          </p>
        </div>

        {error && (
          <div className="mt-4 w-full text-sm text-red-600 bg-red-50 border border-red-200 rounded-[10px] px-3 py-2">
            {error}
          </div>
        )}

        <div className="mt-6 w-full">
          <OtpInput value={code} onChange={setCode} length={6} disabled={submitting} />
        </div>

        <div className="mt-6 w-full space-y-3">
          <button
            type="button"
            className="w-full h-12 rounded-[10px] font-semibold cursor-pointer disabled:opacity-60"
            style={{ backgroundColor: ready ? "#00553E" : "#DCEFE9", color: ready ? "#FFFFFF" : "#7aa79a" }}
            disabled={!ready}
            onClick={onConfirm}
          >
            {submitting ? "Verification..." : "Confirmer"}
          </button>
          <Link to="/patient/connexion" className="block" onClick={onBack}>
            <button
              type="button"
              className="w-full h-12 rounded-[10px] font-semibold border cursor-pointer"
              style={{ borderColor: "#D1E2DD", color: "#00553E" }}
            >
              Retour
            </button>
          </Link>
        </div>
      </main>
    </div>
  );
}
